import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { useAuth } from "./auth-context";
import { useTheme } from "./theme-context";
import { captureLoginSnapshot, captureThemeSnapshot, clearThemeSnapshot, readLoginSnapshot, applySnapshotToDom } from "./theme-snapshot";

const SettingsCtx = createContext(null);

const FONT_LINK_ID = "analyzio-dynamic-font";
const FAVICON_LINK_ID = "analyzio-dynamic-favicon";

// Tailwind expects --primary as an HSL triplet like "0 65% 48%" (used via hsl(var(--primary))).
// Branding may legitimately ship either a triplet OR a hex — normalize to a valid triplet here.
function hexToHslTriplet(hex) {
    if (!hex || typeof hex !== "string") return null;
    let h = hex.trim().replace(/^#/, "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let H = 0, S = 0; const L = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        S = L > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: H = (g - b) / d + (g < b ? 6 : 0); break;
            case g: H = (b - r) / d + 2; break;
            case b: H = (r - g) / d + 4; break;
            default: break;
        }
        H *= 60;
    }
    return `${Math.round(H)} ${Math.round(S * 100)}% ${Math.round(L * 100)}%`;
}

// Accept either HSL-triplet ("0 65% 48%") or hex ("#DC2626") — return a valid triplet or null.
function normalizeColor(value) {
    if (!value || typeof value !== "string") return null;
    const v = value.trim();
    // Already an HSL triplet: "H S% L%"
    if (/^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/.test(v)) return v;
    // Hex fallback
    if (v.startsWith("#")) return hexToHslTriplet(v);
    return null;
}

function loadGoogleFont(family) {
    if (!family) return;
    let link = document.getElementById(FONT_LINK_ID);
    if (!link) {
        link = document.createElement("link");
        link.id = FONT_LINK_ID;
        link.rel = "stylesheet";
        document.head.appendChild(link);
    }
    const escaped = family.replace(/\s+/g, "+");
    link.href = `https://fonts.googleapis.com/css2?family=${escaped}:wght@400;500;600;700&display=swap`;
}

function applyFavicon(url) {
    // Swap (or remove) the <link rel="icon"> on the document head. When the
    // tenant clears their favicon we leave any pre-existing static favicon
    // alone — we only remove the tag we injected ourselves.
    const existing = document.getElementById(FAVICON_LINK_ID);
    if (!url) {
        if (existing) existing.remove();
        return;
    }
    if (existing) {
        existing.href = url;
        return;
    }
    const link = document.createElement("link");
    link.id = FAVICON_LINK_ID;
    link.rel = "icon";
    link.href = url;
    document.head.appendChild(link);
}

// iter-121.148 — App icon (mobile / PWA) → <link rel="apple-touch-icon">.
const APPLE_ICON_LINK_ID = "analyzio-dynamic-apple-icon";
function applyAppleTouchIcon(url) {
    const existing = document.getElementById(APPLE_ICON_LINK_ID);
    if (!url) { if (existing) existing.remove(); return; }
    if (existing) { existing.href = url; return; }
    const link = document.createElement("link");
    link.id = APPLE_ICON_LINK_ID;
    link.rel = "apple-touch-icon";
    link.href = url;
    document.head.appendChild(link);
}

// iter-121.148 — Global tenant Custom CSS. Injected once and updated on every
// settings refresh so it applies across the whole app (not just the Branding
// preview page) and survives reloads. Cleared on logout via resetBrandingDom.
const CUSTOM_CSS_ID = "tenant-custom-css";
function applyCustomCSS(css) {
    let el = document.getElementById(CUSTOM_CSS_ID);
    if (!el) {
        el = document.createElement("style");
        el.id = CUSTOM_CSS_ID;
        document.head.appendChild(el);
    }
    el.textContent = css || "";
}

// iter-121.130 — Clear every DOM mutation `applyBranding` makes so a
// logout (or a fresh /login mount) starts from a clean slate. Without
// this the previous logged-in session's primary colour / sidebar bg /
// favicon / document.title leaked into /login, causing the "new image
// renders then flips back to prior branding" flicker the SA reported.
//
// We DO NOT touch the dashboard-theme CSS vars (--chart-N, --dash-*)
// because those are layered on top via applyDashboardTheme and a
// logout shouldn't change a tenant's chart palette preference.
export function resetBrandingDom() {
    try {
        applyFavicon(null);
        applyAppleTouchIcon(null);
        applyCustomCSS("");
        const appName = process.env.REACT_APP_BRAND_NAME || "Analyzio";
        const tagline = process.env.REACT_APP_BRAND_TAGLINE || "Unified AI Analytics Platform";
        if (document.title !== `${appName} · ${tagline}`) {
            document.title = `${appName} · ${tagline}`;
        }
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", `${appName} — ${tagline}`);
    } catch (_e) { /* non-fatal */ }
    // Clear every brand-set CSS var on the document root + body styles.
    try {
        const root = document.documentElement;
        const vars = [
            "--brand-primary", "--brand-accent", "--brand-secondary",
            "--brand-fg", "--brand-muted-fg", "--brand-surface", "--brand-bg",
            "--brand-text", "--brand-main-bg",
            "--brand-sidebar-bg", "--brand-sidebar-icon",
            "--brand-heading-font", "--brand-line-height",
            "--brand-font-size", "--analyzio-font",
        ];
        for (const v of vars) root.style.removeProperty(v);
        root.style.fontSize = "";
        root.style.backgroundColor = "";
        root.style.colorScheme = "";
        document.body.style.fontFamily = "";
    } catch (_e) { /* non-fatal */ }
    // Clear the boot-loader brand logo so a logout never leaks it.
    try {
        delete document.documentElement.dataset.brandLogo;
        const bootLogo = document.getElementById("app-boot-logo");
        if (bootLogo) { bootLogo.removeAttribute("src"); bootLogo.style.display = "none"; }
    } catch (_e) { /* non-fatal */ }
}

function applyBranding(branding) {
    if (!branding) return;
    applyFavicon(branding.favicon_url);
    // iter-119h: sync the browser-tab title + meta description to the
    // current tenant brand so a white-label deployment shows their name
    // in bookmarks, history, and shared links. Falls back to the static
    // BRAND_NAME baked at build time when the SA hasn't filled in app_name.
    try {
        const appName = (branding.app_name || "").trim()
            || process.env.REACT_APP_BRAND_NAME
            || "Analyzio";
        const tagline = (branding.tagline || "").trim()
            || process.env.REACT_APP_BRAND_TAGLINE
            || "Unified AI Analytics Platform";
        if (document.title !== `${appName} · ${tagline}`) {
            document.title = `${appName} · ${tagline}`;
        }
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", `${appName} — ${tagline}`);
    } catch (_e) { /* non-fatal */ }
    // iter-110.9: broadcast the in-flight branding payload so any component
    // that wants to mirror non-CSS-driven properties (app_name, logo_url) in
    // its render can subscribe. Sidebar listens to this event so the live
    // preview on Settings → Branding reflects app_name/logo edits BEFORE the
    // SA clicks save (or the auto-save fires + settings reloads).
    try {
        window.dispatchEvent(new CustomEvent("branding:preview", { detail: branding }));
    } catch (_e) { /* non-fatal */ }
    // iter-121.147 — Branding vs Dashboard Theme separation (Phase 1).
    // applyBranding NO LONGER writes the base semantic tokens inline
    // (--primary/--ring/--accent/--foreground/--background). Those inline
    // writes won over the .dark stylesheet class, so a tenant's light-mode
    // palette clobbered Dashboard Theme's dark mode. We now write ONLY
    // namespaced --brand-* vars; index.css maps them onto the semantic
    // tokens (identity colours in both modes, surfaces in light mode only)
    // so Branding and Dashboard Theme stay fully independent.
    const primary = normalizeColor(branding.primary_color);
    if (primary) {
        document.documentElement.style.setProperty("--brand-primary", primary);
    } else {
        document.documentElement.style.removeProperty("--brand-primary");
    }
    const accent = normalizeColor(branding.accent_color);
    if (accent) {
        document.documentElement.style.setProperty("--brand-accent", accent);
    } else {
        document.documentElement.style.removeProperty("--brand-accent");
    }
    // iter-121.148 — Brand palette (identity). Each maps to a semantic token
    // in :root:not(.dark) (light mode only); dark mode keeps the theme's own
    // surfaces. secondary_color now drives --secondary (its true shadcn role);
    // text_primary_color owns the foreground text; text_secondary_color drives
    // muted foreground; surface_color drives card/popover surfaces.
    const secondary = normalizeColor(branding.secondary_color);
    if (secondary) {
        document.documentElement.style.setProperty("--brand-secondary", secondary);
    } else {
        document.documentElement.style.removeProperty("--brand-secondary");
    }
    const textPrimary = normalizeColor(branding.text_primary_color);
    if (textPrimary) {
        document.documentElement.style.setProperty("--brand-fg", textPrimary);
        document.documentElement.style.setProperty("--brand-text", textPrimary);
    } else {
        document.documentElement.style.removeProperty("--brand-fg");
        document.documentElement.style.removeProperty("--brand-text");
    }
    const textSecondary = normalizeColor(branding.text_secondary_color);
    if (textSecondary) {
        document.documentElement.style.setProperty("--brand-muted-fg", textSecondary);
    } else {
        document.documentElement.style.removeProperty("--brand-muted-fg");
    }
    const surface = normalizeColor(branding.surface_color);
    if (surface) {
        document.documentElement.style.setProperty("--brand-surface", surface);
    } else {
        document.documentElement.style.removeProperty("--brand-surface");
    }
    // Main page background — light mode only (dark mode keeps its own bg).
    const mainBg = normalizeColor(branding.main_bg_color);
    if (mainBg) {
        document.documentElement.style.setProperty("--brand-bg", mainBg);
        document.documentElement.style.setProperty("--brand-main-bg", `hsl(${mainBg})`);
    } else {
        document.documentElement.style.removeProperty("--brand-bg");
        document.documentElement.style.removeProperty("--brand-main-bg");
    }
    const sidebarBg = normalizeColor(branding.sidebar_bg_color);
    if (sidebarBg) {
        document.documentElement.style.setProperty("--brand-sidebar-bg", `hsl(${sidebarBg})`);
    } else {
        document.documentElement.style.removeProperty("--brand-sidebar-bg");
    }
    const sidebarIcon = normalizeColor(branding.sidebar_icon_color);
    if (sidebarIcon) {
        document.documentElement.style.setProperty("--brand-sidebar-icon", `hsl(${sidebarIcon})`);
    } else {
        document.documentElement.style.removeProperty("--brand-sidebar-icon");
    }
    // Font size scale: sm/md/lg/xl maps to root font-size which Tailwind's
    // rem-based utilities (text-xs … text-4xl) all rescale against.
    // iter-121.148 — read base_font_size (current Typography UI) with the
    // legacy font_size_scale as fallback so the control actually applies.
    const FS = { sm: "13px", md: "14px", lg: "16px", xl: "18px" };
    const scale = branding.base_font_size || branding.font_size_scale;
    if (scale && FS[scale]) {
        document.documentElement.style.setProperty("--brand-font-size", FS[scale]);
        document.documentElement.style.fontSize = FS[scale];
    } else {
        document.documentElement.style.removeProperty("--brand-font-size");
        document.documentElement.style.fontSize = "";
    }
    if (branding.font_family) {
        loadGoogleFont(branding.font_family);
        document.documentElement.style.setProperty("--analyzio-font", `"${branding.font_family}", "Instrument Sans", sans-serif`);
        document.body.style.fontFamily = `"${branding.font_family}", "Instrument Sans", sans-serif`;
    } else {
        document.documentElement.style.removeProperty("--analyzio-font");
        document.body.style.fontFamily = "";
    }
    // iter-121.148 — Heading font (identity). Falls back to the body font.
    if (branding.heading_font) {
        loadGoogleFont(branding.heading_font);
        document.documentElement.style.setProperty("--brand-heading-font", `"${branding.heading_font}", var(--analyzio-font, "Instrument Sans", sans-serif)`);
    } else {
        document.documentElement.style.removeProperty("--brand-heading-font");
    }
    // Body line-height.
    const LH = { tight: "1.35", normal: "1.55", loose: "1.75" };
    if (branding.line_height && LH[branding.line_height]) {
        document.documentElement.style.setProperty("--brand-line-height", LH[branding.line_height]);
    } else {
        document.documentElement.style.removeProperty("--brand-line-height");
    }
    // App icon (mobile / PWA).
    applyAppleTouchIcon(branding.app_icon_url);
    // Boot loader brand sync — keep the (possibly still-visible) branded app
    // loader in step with the resolved brand so it shows the org logo before
    // the app reveals; stash the logo so the theme snapshot / <head> bootstrap
    // can paint it instantly on the next refresh. Logo comes ONLY from
    // Brand Identity config (no hardcoded asset).
    try {
        const logo = branding.logo_url || "";
        document.documentElement.dataset.brandLogo = logo;
        const bootLogo = document.getElementById("app-boot-logo");
        if (bootLogo) {
            if (logo) { bootLogo.src = logo; bootLogo.style.display = ""; }
            else { bootLogo.removeAttribute("src"); bootLogo.style.display = "none"; }
        }
    } catch (_e) { /* non-fatal */ }
}

// iter-121.94 — Dashboard theme application. Translates the tenant's
// stored `dashboard_theme` config into CSS variables on :root + data-
// attributes on <body>. Consumed by every dashboard widget so the same
// SA toggle drives the live UI without per-component plumbing.
//
// Inputs:
//   - cfg: { theme_mode, skin, chart_palette, card_style, table_style,
//            border_radius, shadows, gradient_charts, animations }
//   - registry: full options map from GET /api/settings/dashboard-theme
//
// Side effects on the document:
//   • CSS variables: --radius, --dash-card-bg/border/shadow,
//     --chart-1...--chart-N, --dash-shadow.
//   • Body data-attrs: data-skin, data-table-style, data-card-style,
//     data-gradient-charts, data-animations.
function applyDashboardTheme(cfg, registry) {
    if (!cfg || !registry) return;
    // iter-121.147 — Broadcast the effective theme_mode so ThemeProvider can
    // apply the org/platform dark|light default on fresh sessions. A user's
    // explicit per-user toggle still wins. Decoupled via a window event
    // because ThemeProvider sits above SettingsProvider in the tree.
    if (["light", "dark", "system"].includes(cfg.theme_mode)) {
        try {
            window.dispatchEvent(new CustomEvent("analyzio:theme-mode", { detail: cfg.theme_mode }));
        } catch (_e) { /* non-fatal */ }
    }
    const root = document.documentElement;
    const body = document.body;
    const find = (key, val) => (registry[key]?.options || []).find((o) => o.key === val);

    // Skin → card CSS variables.
    const skin = find("skin", cfg.skin);
    if (skin?.css) {
        Object.entries(skin.css).forEach(([k, v]) => root.style.setProperty(k, v));
    }
    body.setAttribute("data-skin", cfg.skin || "default");

    // Border radius — overrides the base shadcn `--radius` var so every
    // rounded-* utility honours the SA's preference.
    const br = find("border_radius", cfg.border_radius);
    if (br?.css) {
        Object.entries(br.css).forEach(([k, v]) => root.style.setProperty(k, v));
    }

    // Chart palette → --chart-1..N variables. Recharts + custom
    // chart components read these directly.
    // iter-121.96 — `custom_chart_colors` (a list of HEX strings) takes
    // precedence over the named palette when non-empty. This lets users
    // hand-pick per-slot colours instead of choosing a preset palette.
    const customColors = Array.isArray(cfg.custom_chart_colors) ? cfg.custom_chart_colors.filter(Boolean) : [];
    if (customColors.length > 0) {
        customColors.forEach((c, i) => root.style.setProperty(`--chart-${i + 1}`, c));
        root.style.setProperty("--chart-palette", customColors.join(","));
    } else {
        const palette = find("chart_palette", cfg.chart_palette);
        if (palette?.colors) {
            palette.colors.forEach((c, i) => root.style.setProperty(`--chart-${i + 1}`, c));
            root.style.setProperty("--chart-palette", palette.colors.join(","));
        }
    }

    // Shadow scale.
    const SHADOWS = {
        none:   "none",
        subtle: "0 1px 2px rgba(0,0,0,0.04)",
        medium: "0 4px 12px -2px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.06)",
        strong: "0 16px 32px -8px rgba(0,0,0,0.20), 0 8px 16px -4px rgba(0,0,0,0.12)",
    };
    root.style.setProperty("--dash-shadow", SHADOWS[cfg.shadows] || SHADOWS.medium);

    // Card / table styles as body data-attrs (CSS selectors handle the rest).
    body.setAttribute("data-card-style", cfg.card_style || "rounded");
    body.setAttribute("data-table-style", cfg.table_style || "clean");
    body.setAttribute("data-gradient-charts", cfg.gradient_charts === false ? "false" : "true");
    body.setAttribute("data-animations", cfg.animations === false ? "false" : "true");

    // iter-121.148 — Buttons are owned by Dashboard Theme (structural).
    // button_style → --btn-radius (consumed by the Button component);
    // button_text_style → data-btn-caps (CSS uppercases button labels).
    const btn = find("button_style", cfg.button_style);
    if (btn?.css) {
        Object.entries(btn.css).forEach(([k, v]) => root.style.setProperty(k, v));
    } else {
        root.style.setProperty("--btn-radius", "0.375rem");
    }
    body.setAttribute("data-btn-caps", cfg.button_text_style === "uppercase" ? "true" : "false");
}

export function SettingsProvider({ children }) {
    const { user } = useAuth();
    const { setDefaultTheme } = useTheme();
    const [settings, setSettings] = useState(null);
    const [rbac, setRbac] = useState(null);
    // Runtime analytics/tracking config (effective, platform→org merged).
    // Consumed by the isolated TrackingRouter — kept here so the config is
    // read from the SAME runtime settings source as branding/theme.
    const [tracking, setTracking] = useState(null);
    // Header/Footer custom scripts (effective, platform→org merged). Read from
    // the SAME runtime settings source as branding/tracking so the global
    // ScriptsInjector can place them on every page, pre- and post-login.
    const [scripts, setScripts] = useState(null);
    // First-settings-resolution flag. Flips true once the initial
    // settings/branding/dashboard-theme fetch settles (success OR failure)
    // for the current auth state. Drives dismissal of the branded app
    // boot loader (AppBootGate) — so a failed fetch can never wedge it open.
    const [initialized, setInitialized] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const [s, r, dt] = await Promise.all([
                api.get("/settings"),
                api.get("/settings/rbac-matrix"),
                api.get("/settings/dashboard-theme").catch(() => null),
            ]);
            setSettings(s.data);
            setRbac(r.data);
            setTracking(s.data?.effective_tracking || s.data?.tracking || null);
            setScripts(s.data?.effective_scripts || null);
            // iter-121.130 — Use the platform→tenant merged result so
            // OAs see SA-promoted branding for any field they haven't
            // overridden. Falls back to the raw `branding` doc for any
            // legacy code path that still passes it directly.
            applyBranding(s.data?.effective_branding || s.data?.branding);
            // iter-121.148 — apply tenant Custom CSS globally (all pages).
            applyCustomCSS((s.data?.effective_branding || s.data?.branding)?.custom_css);
            // iter-121.94 — apply the tenant's stored dashboard theme
            // iter-121.96 — prefer the effective_config (tenant base
            // merged with the per-user override) so each user sees their
            // personal preference layered on top of the org theme.
            if (dt?.data?.registry) {
                const cfgToApply = dt.data?.effective_config || dt.data?.config;
                if (cfgToApply) {
                    setDefaultTheme(cfgToApply.theme_mode);
                    applyDashboardTheme(cfgToApply, dt.data.registry);
                }
            }
            // Approach A — persist the resolved theme so the inline <head>
            // bootstrap can paint it before React mounts on the next load.
            captureThemeSnapshot();
        } catch (e) {
            // not authenticated yet
        } finally {
            setInitialized(true);
        }
    }, [setDefaultTheme]);

    useEffect(() => {
        if (user) {
            refresh();
        } else {
            // iter-121.151 — Anonymous (logged-out / pre-login) state.
            // The <head> bootstrap already painted the cached PUBLIC login
            // brand before React mounted. Here we (1) drop any stale
            // authenticated theme snapshot, (2) clear any leaked authenticated
            // org brand from the DOM, then (3) immediately repaint the cached
            // login brand in the SAME synchronous effect so /login never
            // flashes back to the platform defaults while the fresh
            // /branding/lookup request is in flight. First-ever visit (no
            // cache) falls back to the clean slate + lookup fills it in.
            let cancelled = false;
            setInitialized(false);
            setSettings(null);
            setRbac(null);
            setTracking(null);
            setScripts(null);
            clearThemeSnapshot();
            resetBrandingDom();
            const loginSnap = readLoginSnapshot();
            if (loginSnap) applySnapshotToDom(loginSnap);
            // The anonymous app has no authenticated /settings request. Its
            // platform default must therefore be resolved here, before the
            // boot gate reveals Login or Signup.
            const initializePublicTheme = async () => {
                try {
                    const response = await api.get("/branding/lookup");
                    if (cancelled || !response?.data) return;
                    const branding = response.data;
                    applyBranding(branding);
                    setTracking(branding?.tracking || null);
                    setScripts({
                        header_scripts: branding?.header_scripts || "",
                        footer_scripts: branding?.footer_scripts || "",
                    });
                    setDefaultTheme(branding.theme_mode);
                    const systemDark = branding.theme_mode === "system"
                        && window.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
                    captureLoginSnapshot({
                        forceDark: branding.theme_mode === "dark" || systemDark,
                    });
                } catch {
                    // ThemeProvider's final Light fallback remains in effect
                    // only when no public platform configuration is available.
                } finally {
                    if (!cancelled) setInitialized(true);
                }
            };
            initializePublicTheme();
            return () => { cancelled = true; };
        }
    }, [user, refresh, setDefaultTheme]);

    // Live RBAC propagation: re-fetch the matrix whenever the tab regains
    // focus or the browser becomes visible again. This is how OA / Viewer
    // sessions pick up changes the SA made in another tab/browser without
    // requiring a manual page reload. Cheap (one GET, ~1KB).
    useEffect(() => {
        if (!user) return;
        const onFocus = () => { refresh(); };
        const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [user, refresh]);

    // Helper: can current role access module? value is "A","W","R","-"
    // iter-121.117 — Reads from `effective_matrix` first (Platform → Org
    // merged) so an Org that hasn't overridden a cell still inherits the
    // SA-set Platform default. Falls back to `matrix` for back-compat
    // until every consumer of the context migrates.
    const canAccess = useCallback((module) => {
        if (!user) return false;
        if (user.role === "super_admin") return true;
        const roleMatrix = rbac?.effective_matrix?.[user.role] || rbac?.matrix?.[user.role];
        if (!roleMatrix) return true; // fail-open if role missing from matrix
        const v = roleMatrix[module];
        if (v === undefined) return true; // fail-open for unknown modules
        return v !== "-";
    }, [user, rbac]);

    const getAccessLevel = useCallback((module) => {
        if (!user) return "-";
        if (user.role === "super_admin") return "A";
        const roleMatrix = rbac?.effective_matrix?.[user.role] || rbac?.matrix?.[user.role];
        if (!roleMatrix) return "A"; // fail-open if role missing from matrix
        const v = roleMatrix[module];
        if (v === undefined) return "A"; // fail-open for unknown modules
        return v;
    }, [user, rbac]);

    return (
        <SettingsCtx.Provider value={{ settings, rbac, tracking, scripts, initialized, refresh, setSettings, setRbac, canAccess, getAccessLevel }}>
            {children}
        </SettingsCtx.Provider>
    );
}

export const useSettings = () => useContext(SettingsCtx);
export { applyBranding, applyDashboardTheme };
